import { Eye } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import {
  StatusBadge,
  type StatusBadgeVariant,
} from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useEnterprise } from "@/contexts/EnterpriseContext";
import { formatDisplayDate } from "@/lib/dateFormat";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import {
  listStaffRequests,
  type EnterpriseStaffRequestView,
} from "@/services/enterpriseRequestsService";

const COLUMN_COUNT = 5;

const STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  pending: "warning",
  in_review: "info",
  awaiting_action: "warning",
  approved: "success",
  rejected: "danger",
  completed: "success",
  cancelled: "danger",
  needs_info: "warning",
};

const STATUS_KEYS = ["pending", "in_review", "awaiting_action", "approved", "rejected", "completed", "cancelled", "needs_info"] as const;

function statusLabel(
  status: string,
  t: (k: string) => string,
): string {
  const KEY_MAP: Record<string, string> = {
    pending: "enterpriseStaff.requests.statusPending",
    in_review: "enterpriseStaff.requests.statusInReview",
    awaiting_action: "enterpriseStaff.requests.statusAwaitingAction",
    approved: "enterpriseStaff.requests.statusApproved",
    rejected: "enterpriseStaff.requests.statusRejected",
    completed: "enterpriseStaff.requests.statusCompleted",
    cancelled: "enterpriseStaff.requests.statusCancelled",
    needs_info: "enterpriseStaff.requests.statusNeedsInfo",
  };
  return t(KEY_MAP[status] ?? status);
}

const ALL_STATUSES_VALUE = "__all__";

export function EnterpriseStaffRequestsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { subdomain } = useEnterprise();
  const navigate = useNavigate();
  const pagination = usePaginationFromUrl();
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES_VALUE);

  const requestsQuery = useQuery({
    queryKey: ["enterprise-staff-requests", subdomain],
    queryFn: () => listStaffRequests(subdomain),
  });

  useEffect(() => {
    if (requestsQuery.isError) {
      toast.error(t("enterpriseStaff.requests.loadError"));
    }
  }, [requestsQuery.isError, t]);

  const allRequests = requestsQuery.data ?? [];

  const HIDDEN_STATUSES = new Set(["cancelled"]);

  const filteredRequests = useMemo(() => {
    if (statusFilter === ALL_STATUSES_VALUE) {
      return allRequests.filter((r) => !HIDDEN_STATUSES.has(r.status));
    }
    return allRequests.filter((r) => r.status === statusFilter);
  }, [allRequests, statusFilter]);

  useEffect(() => {
    pagination.setTotal(filteredRequests.length);
  }, [filteredRequests.length, pagination]);

  const paginatedRequests = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filteredRequests.slice(start, start + pagination.limit);
  }, [filteredRequests, pagination.page, pagination.limit]);

  const handleRefresh = (): void => {
    void requestsQuery.refetch();
  };

  const buildRowActions = useCallback(
    (req: EnterpriseStaffRequestView): TableAction[] => [
      {
        label: t("enterpriseStaff.requests.viewDetails"),
        icon: Eye,
        onClick: () => navigate(`/requests/${req.id}`),
      },
    ],
    [t, navigate],
  );

  const tableHeader = (
    <TableHeader>
      <TableRow>
        <TableHead>{t("enterpriseStaff.requests.columns.created")}</TableHead>
        <TableHead>{t("enterpriseStaff.requests.columns.product")}</TableHead>
        <TableHead>{t("enterpriseStaff.requests.columns.status")}</TableHead>
        <TableHead>{t("enterpriseStaff.requests.columns.applicant")}</TableHead>
        <TableHead className="w-[80px]">
          {t("platformEnterprises.colActions")}
        </TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <div className="p-4 md:p-8">
      <PageHeader title={t("enterpriseStaff.requests.listTitle")} />

      <div className="mt-4 flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t("enterpriseStaff.requests.filterStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES_VALUE}>
              {t("enterpriseStaff.requests.filterAllStatuses")}
            </SelectItem>
            {STATUS_KEYS.map((s) => (
              <SelectItem key={s} value={s}>
                {statusLabel(s, t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <TableCard className="mt-4">
        {requestsQuery.isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : (
          <>
            {filteredRequests.length === 0 ? (
              <ResponsiveListSplit
                table={
                  <Table>
                    {tableHeader}
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={COLUMN_COUNT} className="h-32">
                          <EmptyState
                            title={t("enterpriseStaff.requests.empty")}
                            message={t("enterpriseStaff.requests.emptyMessage")}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                }
                cards={
                  <div className="flex min-h-[40vh] items-center justify-center p-4">
                    <EmptyState
                      title={t("enterpriseStaff.requests.empty")}
                      message={t("enterpriseStaff.requests.emptyMessage")}
                    />
                  </div>
                }
              />
            ) : (
              <ResponsiveListSplit
                table={
                  <Table>
                    {tableHeader}
                    <TableBody>
                      {paginatedRequests.map((req) => (
                        <TableRow
                          key={req.id}
                          className="cursor-pointer transition-colors duration-150 hover:bg-muted/50"
                          onClick={() => navigate(`/requests/${req.id}`)}
                        >
                          <TableCell className="text-muted-foreground">
                            {formatDisplayDate(req.createdAt, { locale: i18n.language })}
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            {req.product?.displayName ?? req.productSlug}
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              variant={STATUS_VARIANT[req.status] ?? "warning"}
                            >
                              {statusLabel(req.status, t)}
                            </StatusBadge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {req.user
                              ? [req.user.firstName, req.user.lastName].filter(Boolean).join(" ").trim() || req.user.email
                              : req.userId.slice(0, 8)}
                          </TableCell>
                          <TableCell>
                            <TableActionsMenu actions={buildRowActions(req)} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                }
                cards={
                  <div className="divide-y divide-border">
                    {paginatedRequests.map((req) => (
                      <DataListCardRow
                        key={req.id}
                        onClick={() => navigate(`/requests/${req.id}`)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="min-w-0 flex-1 font-semibold leading-snug text-foreground">
                            {req.product?.displayName ?? req.productSlug}
                          </p>
                          <div className="shrink-0">
                            <TableActionsMenu actions={buildRowActions(req)} />
                          </div>
                        </div>
                        <div className="mt-3 space-y-3">
                          <DataListCardField
                            label={t("enterpriseStaff.requests.columns.status")}
                          >
                            <StatusBadge
                              variant={STATUS_VARIANT[req.status] ?? "warning"}
                            >
                              {statusLabel(req.status, t)}
                            </StatusBadge>
                          </DataListCardField>
                          <DataListCardField
                            label={t("enterpriseStaff.requests.columns.created")}
                          >
                            <span className="text-muted-foreground">
                              {formatDisplayDate(req.createdAt, { locale: i18n.language })}
                            </span>
                          </DataListCardField>
                          <DataListCardField
                            label={t("enterpriseStaff.requests.columns.applicant")}
                          >
                            <span className="text-muted-foreground">
                              {req.user
                              ? [req.user.firstName, req.user.lastName].filter(Boolean).join(" ").trim() || req.user.email
                              : req.userId.slice(0, 8)}
                            </span>
                          </DataListCardField>
                        </div>
                      </DataListCardRow>
                    ))}
                  </div>
                }
              />
            )}
            {filteredRequests.length > 0 && (
              <TablePagination
                pagination={pagination}
                rowsPerPageLabel={t("platformEnterprises.rowsPerPage")}
                pageOfLabel={t("platformEnterprises.pageOf", {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  ),
                  total: pagination.total,
                })}
                onRefresh={handleRefresh}
                isRefreshing={requestsQuery.isFetching}
              />
            )}
          </>
        )}
      </TableCard>
    </div>
  );
}
